// @ts-nocheck
/* Starter Custom File */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { NumberTextFieldValue } from "@store/Framework/Common/Fields/Toolkit/numberText";
import { TextAlignment, textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import CarouselArrow, {
    CarouselArrowPresentationProps,
    CarouselArrowProps,
} from "@store/Library/Components/Toolkit/Carousel/CarouselArrow";
import CarouselButton, {
    CarouselButtonPresentationProps,
} from "@store/Library/Components/Toolkit/Carousel/CarouselButton";
import CarouselDots, {
    CarouselDotProps,
    CarouselDotsPresentationProps,
} from "@store/Library/Components/Toolkit/Carousel/CarouselDots";
import Pause from "@store/Library/Components/Toolkit/Icons/Pause";
import Play from "@store/Library/Components/Toolkit/Icons/Play";
import useCurrentBreakpointValue from "@toolkit/Framework/Common/Hooks/useCurrentBreakpointValue";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { BreakpointValue, BreakpointValueProp } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { DataOption, DataOptions, getDataOptionValue } from "@toolkit/Framework/Common/Utilities/dataOptions";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import useRecursiveTimeout from "@toolkit/Modules/client-framework/Common/Hooks/useRecursiveTimeout";
import { DataType } from "csstype";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import React, {
    createRef,
    FC,
    HTMLAttributes,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { css } from "styled-components";

const defaultAxis: CarouselProps["axis"] = "x";
const defaultDuration: CarouselProps["duration"] = 35; // Only values between 20-60 are recommended. Higher numbers enables slower scrolling.
const defaultItemsPerSlide: CarouselProps["itemsPerSlide"] = 1;
const mobileBreakpoint: BreakpointKey = "xs";

export type CarouselAlign = "center" | "end" | "start";
export type CarouselAxis = "x" | "y";
export type CarouselItemsAlign = "flex-start" | "center" | "flex-end" | "stretch";
export type CarouselItemsJustify = "flex-start" | "center" | "flex-end" | DataType.ContentDistribution;

export type CarouselArrowsPositionVariantOption = DataOption<CarouselArrowPresentationProps>;
export interface CarouselArrowsPositionVariantOptionMapper {
    standard: CarouselArrowsPositionVariantOption;
    inside: CarouselArrowsPositionVariantOption;
}
export type CarouselArrowsPositionVariantOptions = DataOptions<CarouselArrowsPositionVariantOption> &
    CarouselArrowsPositionVariantOptionMapper;
export type CarouselArrowsPositionVariant = keyof CarouselArrowsPositionVariantOptionMapper;

export const carouselArrowsPositionVariantOptions: CarouselArrowsPositionVariantOptions = {
    standard: {
        id: "standard",
        displayName: "Standard",
        isDefault: true,
        value: {
            positionOrigin: ["50%", "60%"],
        },
    },
    inside: { id: "inside", displayName: "Inside", isDefault: true, value: {} },
};

export const getItemsPerSlidePreset = (maxNumber: number | undefined) => {
    if (!maxNumber) {
        return undefined;
    }

    switch (maxNumber) {
        case 1:
            return [1, 1, 1, 1, 1];
        case 2:
            return [1, 2, 2, 2, 2];
        case 3:
            return [1, 2, 2, 3, 3];
        case 4:
            return [1, 2, 3, 4, 4];
        case 5:
            return [1, 2, 3, 4, 5];
        case 6:
            return [1, 3, 4, 5, 6];
        default:
            return [1, 2, 3, 4, maxNumber];
    }
};

export const getItemsPerSlideFromFieldValue = (
    value: NumberTextFieldValue,
    parser: (...args: any) => BreakpointValue<number>[] | undefined,
) => {
    const number = Number(value);

    if (!Number.isNaN(number)) {
        return getItemsPerSlidePreset(number);
    }

    return parser(value);
};

export interface CarouselPresentationProps extends ExpandStyles<CarouselStyles> {
    align?: CarouselAlign;
    arrowsGap?: BreakpointValueProp;
    arrowsPositionVariant?: CarouselArrowsPositionVariant;
    autoplay?: boolean;
    autoplayDelay?: number;
    autoplayStopOnInteraction?: boolean;
    axis?: BreakpointValueProp<CarouselAxis>;
    duration?: number;
    inlineArrowsBreakpoint?: BreakpointKey;
    invertColor?: boolean | number[];
    itemsAlign?: BreakpointValueProp<CarouselItemsAlign>;
    itemsGap?: BreakpointValueProp;
    itemsJustify?: BreakpointValueProp<CarouselItemsJustify>;
    itemsPerSlide?: BreakpointValueProp<number>;
    itemsSize?: BreakpointValueProp<string>;
    itemsToScroll?: BreakpointValueProp<number>;
    keepArrows?: boolean;
    keepInvisibleArrows?: boolean;
    loop?: boolean;
    maxNumberOfColumns?: number;
    showArrows?: boolean;
    showDots?: boolean;
    startIndex?: number;
    titleAlignment?: TextAlignment;
    titleId?: string;
    peekNextSlide?: boolean;
}

export interface CarouselProps extends CarouselPresentationProps, HTMLAttributes<HTMLDivElement> {
    id: string;
    slides: CarouselSlide[];
    title?: string;
    useSkipNav?: boolean;
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options?: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    skipCarouselButton?: SkipNavStyles;
    carouselControls?: InjectableCss;
    carouselPlay?: CarouselButtonPresentationProps;
    carouselPause?: CarouselButtonPresentationProps;
    carouselDots?: CarouselDotsPresentationProps;
    carouselArrowPrevious?: CarouselArrowPresentationProps;
    carouselArrowNext?: CarouselArrowPresentationProps;
    carouselArrowInvisible?: CarouselArrowPresentationProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
}

export const carouselStyles: CarouselStyles = {
    titleText: { variant: "h2" },
    wrapper: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
        `,
    },
    carouselControls: {
        css: css`
            display: flex;
            align-self: center;
            align-items: center;
            gap: ${pxToRem(16)};
            order: 1;
            z-index: 1;
            margin-top: ${pxToRem(24)};

            &::before {
                content: "";
                order: -1;
            }

            &::after {
                content: "";
                order: 99;
            }
        `,
    },
    carouselPlay: {
        iconSrc: Play,
        iconMargin: "0 0 0 0.08em",
    },
    carouselPause: { iconSrc: Pause },
    carouselDots: {
        extendedStyles: {
            wrapper: {
                css: css`
                    order: 2;
                `,
            },
        },
    },
    carouselArrowPrevious: {
        extendedStyles: {
            carouselButton: {
                extendedStyles: {
                    wrapper: {
                        css: css`
                            align-self: center;
                            order: 1;
                        `,
                    },
                },
            },
        },
    },
    carouselArrowNext: {
        extendedStyles: {
            carouselButton: {
                extendedStyles: {
                    wrapper: {
                        css: css`
                            align-self: center;
                            order: 3;
                        `,
                    },
                },
            },
        },
    },
    carouselArrowInvisible: {
        extendedStyles: {
            carouselButton: {
                extendedStyles: {
                    wrapper: {
                        css: css`
                            visibility: hidden;
                            pointer-events: none;
                        `,
                    },
                },
            },
        },
    },
    carouselWrapper: {
        css: css`
            position: relative;
            display: flex;
            flex-grow: 1;
            min-width: 0;
            min-height: 0;
        `,
    },
    carouselContainer: {
        css: css`
            position: relative;
            overflow: hidden;
            display: flex;
            flex-grow: 1;
            order: 2;
            min-width: 0;
            min-height: 0;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            flex-grow: 1;
            backface-visibility: hidden;
            min-width: 0;
            min-height: 0;
        `,
    },
    carouselSlide: {
        css: css`
            position: relative;
            flex: 0 0 auto;
            min-width: 0;
            min-height: 0;
        `,
    },
};

const Carousel: FC<CarouselProps> = ({
    align = "start",
    arrowsGap: baseArrowsGap,
    arrowsPositionVariant = "standard",
    autoplay,
    autoplayDelay = 4000,
    autoplayStopOnInteraction = true,
    axis: baseAxis = defaultAxis,
    children,
    className,
    duration = defaultDuration,
    extendedStyles,
    id,
    inlineArrowsBreakpoint,
    invertColor: baseInvertColor,
    itemsAlign: baseItemsAlign,
    itemsGap: baseItemsGap = pxToRem(30),
    itemsJustify: baseitemsJustify,
    itemsPerSlide: baseItemsPerSlide = defaultItemsPerSlide,
    itemsSize: baseItemsSize,
    itemsToScroll: baseItemsToScroll,
    keepArrows: baseKeepArrows,
    keepInvisibleArrows,
    loop = true,
    maxNumberOfColumns,
    overridedStyles,
    showArrows = true,
    showDots,
    slides,
    startIndex = 0,
    title,
    titleAlignment = textAlignmentOptions.center.id,
    titleId,
    useSkipNav = true,
    peekNextSlide = false,
    ...htmlAttributes
}) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const arrowsGap = useCurrentBreakpointValue(baseArrowsGap);
    const arrowsPositionVariantProps = getDataOptionValue(carouselArrowsPositionVariantOptions, arrowsPositionVariant);
    const [autoplayPaused, setAutoplayPaused] = useState(false);
    const axis = useCurrentBreakpointValue(baseAxis) ?? defaultAxis;
    const carouselTitleId = titleId || `carousel-title-${id}`;
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const { isEditing } = useShellContext();
    const isInlineArrows = useIsBreakpoint(inlineArrowsBreakpoint, "down");
    const itemsAlign = useCurrentBreakpointValue(baseItemsAlign);
    const itemsPerSlide =
        useCurrentBreakpointValue(getItemsPerSlidePreset(maxNumberOfColumns) || baseItemsPerSlide) ??
        defaultItemsPerSlide;
    const itemsJustify = useCurrentBreakpointValue(baseitemsJustify);
    const itemsGap = useCurrentBreakpointValue(baseItemsGap);
    const itemsSize = useCurrentBreakpointValue(baseItemsSize);
    const hasItemsSize = Boolean(itemsSize && itemsSize !== "auto");
    const keepArrows = baseKeepArrows || keepInvisibleArrows;
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [snapList, setSnapList] = useState<number[]>();
    const styles = useExpandProps(carouselStyles, extendedStyles, overridedStyles);

    let itemsToScroll = useCurrentBreakpointValue(baseItemsToScroll) ?? itemsPerSlide;
    if (itemsToScroll > itemsPerSlide || itemsToScroll < 1) {
        itemsToScroll = itemsPerSlide;
    }

    const autoplayEnabled = Boolean(autoplay && !isEditing);
    const hasMultipleSlides = slides.length > itemsPerSlide;
    const invertColor = Array.isArray(baseInvertColor) ? baseInvertColor.includes(selectedIndex) : baseInvertColor;
    const isArrowInside = arrowsPositionVariant === "inside";
    const isVertical = axis === "y";

    const hasInlineArrows = Boolean(showArrows && isInlineArrows);
    const showInlineArrows = Boolean(hasInlineArrows && hasMultipleSlides);

    const hasSideArrows = Boolean(showArrows && !isInlineArrows);
    const hasInvisibleSideArrows = Boolean(hasSideArrows && !hasMultipleSlides && keepInvisibleArrows);
    const showSideArrows = Boolean((hasSideArrows && (hasMultipleSlides || keepArrows)) || hasInvisibleSideArrows);

    const showControls = hasMultipleSlides && (autoplay || showDots || showInlineArrows);
    const hasButtonControls = showControls && (autoplay || showInlineArrows);
    const hasDotControls = showControls && showDots && !hasButtonControls;

    const emblaOptions = useRef<EmblaOptionsType>(undefined);
    const [emblaRef, embla] = useEmblaCarousel();

    const handleAutoplay = useCallback(() => {
        embla?.scrollNext();
    }, [embla]);

    useRecursiveTimeout(handleAutoplay, (autoplayEnabled && !autoplayPaused && autoplayDelay) || 0);

    const pauseAutoplay = useCallback(() => {
        setAutoplayPaused(true);
    }, []);

    const resumeAutoplay = useCallback(() => {
        setAutoplayPaused(false);
    }, []);

    const toggleAutoplay = useCallback(() => {
        if (autoplayPaused) {
            embla?.scrollNext();
            resumeAutoplay();
        } else {
            pauseAutoplay();
        }
    }, [autoplayPaused, embla, pauseAutoplay, resumeAutoplay]);

    const handleInteraction = useCallback(() => {
        autoplayStopOnInteraction && pauseAutoplay();
    }, [autoplayStopOnInteraction, pauseAutoplay]);

    const scrollPrev = useCallback(() => {
        embla?.scrollPrev();
        handleInteraction();
    }, [embla, handleInteraction]);

    const scrollTo = useCallback(
        (index: number) => {
            embla?.scrollTo(index);
            handleInteraction();
        },
        [embla, handleInteraction],
    );

    const scrollNext = useCallback(() => {
        embla?.scrollNext();
        handleInteraction();
    }, [embla, handleInteraction]);

    const onPointerDown = useCallback(() => {
        handleInteraction();
    }, [handleInteraction]);

    useEffect(() => {
        embla?.on("pointerDown", onPointerDown);

        return () => {
            embla?.off("pointerDown", onPointerDown);
        };
    }, [embla, onPointerDown]);

    const onSelect = useCallback(() => {
        if (!embla) {
            return;
        }

        setCanScrollPrev(embla.canScrollPrev());
        setCanScrollNext(embla.canScrollNext());
        setSelectedIndex(embla.selectedScrollSnap());
    }, [embla]);

    useEffect(() => {
        embla?.on("select", onSelect);

        return () => {
            embla?.off("select", onSelect);
        };
    }, [embla, onSelect]);

    const onInit = useCallback(() => {
        if (!embla) {
            return;
        }

        onSelect();
        setSnapList(embla.scrollSnapList());
    }, [embla, onSelect]);

    useEffect(() => {
        embla?.on("reInit", onInit);

        return () => {
            embla?.off("reInit", onInit);
        };
    }, [embla, onInit]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        const options: EmblaOptionsType = {
            align,
            axis,
            watchDrag: !isEditing && isTouchDevice() && hasMultipleSlides,
            loop,
            slidesToScroll: itemsToScroll,
            duration,
            startIndex,
        };

        const previousOptions = emblaOptions.current;
        emblaOptions.current = { ...options };

        if (options.startIndex === previousOptions?.startIndex) {
            delete options["startIndex"];
        }

        embla.reInit(options);
    }, [
        align,
        arrowsGap,
        axis,
        duration,
        embla,
        hasMultipleSlides,
        isEditing,
        itemsAlign,
        itemsGap,
        itemsJustify,
        itemsPerSlide,
        itemsToScroll,
        loop,
        maxNumberOfColumns,
        slides.length,
        startIndex,
    ]);

    const carouselArrowPreviousProps: CarouselArrowProps = useMemo(() => {
        return {
            "aria-hidden": hasInvisibleSideArrows || undefined,
            carouselButton: {
                button: {
                    disabled: !canScrollPrev,
                    onClick: scrollPrev,
                    tabIndex: hasInvisibleSideArrows ? -1 : undefined,
                },
                className: "previous",
                label: translate("Previous slide"),
            },
            direction: isVertical ? "up" : "left",
            position: !isArrowInside ? (isVertical ? "top" : "left") : undefined,
            ...mergeProps<CarouselArrowPresentationProps>(
                mergeProps(arrowsPositionVariantProps, styles.carouselArrowPrevious),
                hasInvisibleSideArrows ? styles.carouselArrowInvisible : undefined,
            ),
        };
    }, [
        canScrollPrev,
        hasInvisibleSideArrows,
        isArrowInside,
        isVertical,
        scrollPrev,
        styles.carouselArrowInvisible,
        styles.carouselArrowPrevious,
    ]);

    const carouselArrowNextProps: CarouselArrowProps = useMemo(() => {
        return {
            "aria-hidden": hasInvisibleSideArrows || undefined,
            carouselButton: {
                button: {
                    disabled: !canScrollNext,
                    onClick: scrollNext,
                    tabIndex: hasInvisibleSideArrows ? -1 : undefined,
                },
                className: "next",
                label: translate("Next slide"),
            },
            direction: isVertical ? "down" : "right",
            position: !isArrowInside ? (isVertical ? "bottom" : "right") : undefined,
            ...mergeProps<CarouselArrowPresentationProps>(
                mergeProps(arrowsPositionVariantProps, styles.carouselArrowNext),
                hasInvisibleSideArrows ? styles.carouselArrowInvisible : undefined,
            ),
        };
    }, [
        canScrollNext,
        hasInvisibleSideArrows,
        isArrowInside,
        isVertical,
        scrollPrev,
        styles.carouselArrowInvisible,
        styles.carouselArrowNext,
    ]);

    const carouselWrapperStyles = useMemo(() => {
        return mergeProps<CarouselStyles["carouselWrapper"]>(styles.carouselWrapper, {
            css: css`
                flex-direction: ${isVertical ? "column" : "row"};
            `,
        });
    }, [isVertical, styles.carouselWrapper]);

    const carouselContainerStyles = useMemo(() => {
        const hasArrowsGap = showSideArrows && arrowsGap;

        if (!hasArrowsGap && !hasItemsSize) {
            return styles.carouselContainer;
        }

        return mergeProps<CarouselStyles["carouselContainer"]>(styles.carouselContainer, {
            css: css`
                ${hasArrowsGap ? `margin: ${isVertical ? `${arrowsGap} 0` : `0 ${arrowsGap}`};` : null}
                ${hasItemsSize
                    ? `max-${isVertical ? "height" : "width"}: calc(${itemsSize} * ${itemsPerSlide}${
                          itemsGap ? ` + (${itemsGap} * ${itemsPerSlide - 1})` : ""
                      });`
                    : null}
            `,
        });
    }, [
        arrowsGap,
        hasItemsSize,
        isVertical,
        itemsGap,
        itemsPerSlide,
        itemsSize,
        showSideArrows,
        styles.carouselContainer,
    ]);

    const carouselSlidesContainerStyles = useMemo(() => {
        return mergeProps<CarouselStyles["carouselSlidesContainer"]>(styles.carouselSlidesContainer, {
            css: css`
                flex-direction: ${isVertical ? "column" : "row"};
                ${cssProp("align-items", itemsAlign)}
                ${itemsGap
                    ? `${`margin: ${isVertical ? `calc(-${itemsGap} / 2) 0` : `0 calc(-${itemsGap} / 2)`}`};`
                    : null}
                ${() => {
                    if (!hasMultipleSlides) {
                        return css`
                            transform: none !important;
                            ${cssProp("justify-content", itemsJustify)}
                        `;
                    }
                }}
            `,
        });
    }, [hasMultipleSlides, itemsAlign, isVertical, itemsGap, itemsJustify, styles.carouselSlidesContainer]);

    const carouselSlideStyles = useMemo(() => {
        return mergeProps<CarouselStyles["carouselSlide"]>(styles.carouselSlide, {
            css: css`
                ${itemsPerSlide ? `flex-basis: calc(100% / ${itemsPerSlide});` : null}
                ${itemsGap
                    ? `${`padding: ${isVertical ? `calc(${itemsGap} / 2) 0` : `0 calc(${itemsGap} / 2)`}`};`
                    : null}
                ${hasItemsSize
                    ? `min-${isVertical ? "height" : "width"}: ${
                          itemsGap ? `calc(${itemsSize} + ${itemsGap})` : itemsSize
                      };`
                    : null}
                ${() => {
                    if (peekNextSlide) {
                        return css`
                            ${breakpoint(mobileBreakpoint, "down")} {
                                flex: 0 0 83%;
                            }
                        `;
                    }
                }}
            `,
        });
    }, [hasItemsSize, isVertical, itemsGap, itemsPerSlide, itemsSize, styles.carouselSlide, peekNextSlide]);

    const titleStyles = useMemo(() => {
        return mergeProps<CarouselStyles["titleText"]>(styles.titleText, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.titleText, titleAlignment]);

    if (!slides.length && !isEditing) {
        return null;
    }

    return (
        <>
            {Boolean(title) && (
                <Typography {...titleStyles} id={carouselTitleId}>
                    {title}
                </Typography>
            )}
            <StyledWrapper
                {...styles.wrapper}
                aria-label=" " // Required to enable "role" behavior
                aria-labelledby={title ? carouselTitleId : undefined}
                aria-roledescription="carousel"
                className={`carousel axis-${axis}${hasDotControls ? " has-dot-controls" : ""}${
                    hasButtonControls ? " has-button-controls" : ""
                }${invertColor ? " invert-color" : ""}${className ? ` ${className}` : ""}`}
                role="group"
                {...htmlAttributes}
            >
                {useSkipNav && (
                    <SkipNav
                        destination={afterCarousel}
                        extendedStyles={styles.skipCarouselButton}
                        text={translate("List of {0} items, skip list?", `${slides.length}`)}
                    />
                )}
                {showControls && (
                    <StyledWrapper
                        {...styles.carouselControls}
                        className={`carousel-controls${hasDotControls ? " has-dot" : ""}${
                            hasButtonControls ? " has-button" : ""
                        }`}
                    >
                        {autoplay && (
                            <CarouselButton
                                {...(autoplayPaused || isEditing ? styles.carouselPlay : styles.carouselPause)}
                                button={{ disabled: isEditing, onClick: toggleAutoplay }}
                                className="play-pause"
                                label={
                                    autoplayPaused
                                        ? translate("Start automatic slide show")
                                        : translate("Stop automatic slide show")
                                }
                            />
                        )}
                        {showDots && (
                            <CarouselDots
                                {...styles.carouselDots}
                                dots={snapList?.map(
                                    (_, index): CarouselDotProps => ({
                                        selected: index === selectedIndex,
                                        button: { onClick: () => scrollTo(index) },
                                    }),
                                )}
                            />
                        )}
                        {showInlineArrows && (
                            <>
                                <CarouselArrow {...carouselArrowPreviousProps} position={undefined} />
                                <CarouselArrow {...carouselArrowNextProps} position={undefined} />
                            </>
                        )}
                    </StyledWrapper>
                )}
                <StyledWrapper className="carousel-wrapper" {...carouselWrapperStyles}>
                    {showSideArrows && (
                        <>
                            <CarouselArrow {...carouselArrowPreviousProps} />
                            <CarouselArrow {...carouselArrowNextProps} />
                        </>
                    )}
                    <StyledWrapper ref={emblaRef} className="carousel-container" {...carouselContainerStyles}>
                        <StyledWrapper {...carouselSlidesContainerStyles} className="carousel-slides-container">
                            {slides.map((slide, index) => {
                                return (
                                    <StyledWrapper
                                        {...carouselSlideStyles}
                                        aria-label={translate("{0} of {1}", `${index + 1}`, `${slides.length}`)}
                                        aria-roledescription="item"
                                        className="carousel-slide"
                                        key={slide.id}
                                        role="group"
                                    >
                                        {slide.renderComponent()}
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                {useSkipNav && <span ref={afterCarousel} tabIndex={-1} />}
            </StyledWrapper>
        </>
    );
};

export const carouselDefaultCustomCss = `.carousel {
}

.carousel-controls {
}

.carousel-wrapper {
}

.carousel-container {
}

.carousel-slides-container {
}

.carousel-slide {
}

.carousel-button-wrapper {
}

.carousel-button-wrapper .button {
}

.carousel-button-wrapper.arrow {
}

.carousel-button-wrapper.arrow .button {
}

.carousel-button-wrapper.arrow.previous {
}

.carousel-button-wrapper.arrow.previous .button {
}

.carousel-button-wrapper.arrow.next {
}

.carousel-button-wrapper.arrow.next .button {
}

.carousel-button-wrapper.play-pause {
}

.carousel-button-wrapper.play-pause .button {
}

.carousel-dot-wrapper {
}

.carousel-dot {
}`;

export default Carousel;
